import { useMemo, useRef, useState } from 'react';
import { CATEGORY_LABEL, type NavResponse, type SearchResponse } from '@photos/shared';
import { useNav, usePhotos } from '../api/hooks';
import { suggestTags } from '../lib/tags';
import { useLibraryState } from '../state/libraryParams';
import { useSession } from '../state/session';
import { Icon } from './Icon';
import { TagChip } from './TagChip';

/** Tag counts known for the whole source (sidebar data). */
export function navTagCounts(nav: NavResponse | undefined): Map<string, number> {
  const m = new Map<string, number>();
  if (!nav) return m;
  for (const list of [nav.years, nav.regions, nav.churches, nav.groups.fellowship, nav.groups.service, nav.popular]) for (const t of list) m.set(t.tag, t.count);
  const eventTotals = new Map<string, number>();
  for (const r of nav.events)
    for (const t of r.tags) if (!/^\d{4}$/.test(t)) eventTotals.set(t, (eventTotals.get(t) ?? 0) + r.count);
  for (const [t, n] of eventTotals) if (!m.has(t)) m.set(t, n);
  return m;
}

/** Co-occurring tags in the current results. */
export function relatedCounts(page: SearchResponse | undefined): Map<string, number> {
  return new Map((page?.related ?? []).map((r) => [r.tag, r.count]));
}

const PLACEHOLDER = 'Search by tag — try “2026”, “summer”, “ya”…';

export function SearchBox() {
  const { state, go } = useLibraryState();
  const active = state.tags;
  const nav = useNav(state.source);
  const photos = usePhotos(
    {
      source: state.source,
      tags: active,
      picksOnly: state.tab === 'picks' || state.picksOnly,
      orient: state.orient === 'all' ? undefined : state.orient,
    },
    active.length > 0,
  );
  const counts = useMemo(
    () => (active.length ? relatedCounts(photos.data?.pages[0]) : navTagCounts(nav.data)),
    [active.length, photos.data, nav.data],
  );
  return (
    <TagSearch
      id="search"
      active={active}
      counts={counts}
      onChange={(tags) => go({ tags, tab: state.tab === 'all' || state.tab === 'picks' ? state.tab : 'all' })}
    />
  );
}

interface TagSearchProps {
  active: string[];
  /** tag → photo count among the current results (suggestions come from here) */
  counts: ReadonlyMap<string, number>;
  onChange(tags: string[]): void;
  id?: string;
  placeholder?: string;
  /** heading over the suggestions before anything is typed */
  scopeLabel?: string;
}

/** Tag chips + autocomplete over the tags in the current results (Browse header, Studio album toolbar). */
export function TagSearch({ active, counts, onChange, id, placeholder = PLACEHOLDER, scopeLabel = 'Popular tags' }: TagSearchProps) {
  const { tagIndex } = useSession();
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const { keys, alias } = useMemo(() => suggestTags(value, counts, active, tagIndex), [value, counts, active, tagIndex]);

  const setActive = onChange;
  const listId = `${id ?? 'tags'}-ac`;

  const add = (t: string) => {
    setValue('');
    setHi(0);
    setActive([...active, t]);
  };

  return (
    <div
      className="search"
      id={id}
      onClick={() => input.current?.focus()}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Icon name="search" style={{ color: 'var(--muted)' }} />
      {active.map((t) => (
        <TagChip key={t} tag={t} onRemove={() => setActive(active.filter((x) => x !== t))} />
      ))}
      <input
        ref={input}
        id={id ? `${id}Input` : undefined}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label="Search by tag"
        placeholder={active.length ? 'Add another tag…' : placeholder}
        value={value}
        onFocus={() => {
          setOpen(true);
          setHi(0);
        }}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
          setHi(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!keys.length) return;
            setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : -1) + keys.length) % keys.length);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const t = keys[hi];
            if (t) add(t);
          } else if (e.key === 'Backspace' && !value && active.length) {
            setActive(active.slice(0, -1));
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            input.current?.blur();
          }
        }}
      />
      <div className={`ac${open ? ' open' : ''}`} id={listId} role="listbox">
        {!keys.length ? (
          <div className="hint">No matching tags{active.length ? ' within current results' : ''}.</div>
        ) : (
          <>
            {!value && (
              <div className="hint">
                {active.length ? 'Narrow down — tags that appear in your results' : scopeLabel}
              </div>
            )}
            {keys.map((t, i) => (
              <div
                key={t}
                role="option"
                aria-selected={i === hi}
                className={`row${i === hi ? ' hi' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(t);
                }}
              >
                <span className="cat">{CATEGORY_LABEL[tagIndex.catOf(t)]}</span>
                <TagChip
                  tag={t}
                  extra={t === alias ? <span className="auto"> alias “{value.trim()}”</span> : undefined}
                />
                <span className="n">{counts.get(t) ?? 0} photos</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
